import { clamp } from "lodash-es";

import { removeBoardSubscriptions } from "@/backend/read/BackendReader";
import { writer } from "@/backend/write/BackendWriter";
import { offScreenCoord } from "@/math/coordinate-utils";
import { maxCardSizeFactor, minCardSizeFactor } from "@/model/Settings";
import type { Board } from "@/model/board";
import type { BoardCard, Card } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { useLinkStore } from "@/store/link";
import { useUserStore } from "@/store/user";

/**
 * These actions are used indirectly by other actions and will never create a tracking event
 */
export const internalActions = {
  switchBoard(board?: Board) {
    const { switched, board: oldBoard, cards } = useBoardStore().switchBoard(board);
    cleanupBoard(oldBoard, cards, switched);
  },

  leaveBoard() {
    const { board, cards } = useBoardStore().leaveBoard();
    cleanupBoard(board, cards, true);
  },

  /**
   * Deselect all selected cards from the given board and
   * send a stopAlter for each card that was deselected
   */
  clearCardSelection(board: Board) {
    clearStickySelection(board);
  },

  cardToFront(cardId: string, boardId?: string) {
    if (useBoardsStore().cardToFront({ id: cardId, boardId })) {
      writer.frontCard(useBoardStore().currentBoard().id, [cardId]);
    }
  },

  markCardLinkedCards(cards: BoardCard[]) {
    useLinkStore()
      .markCardLinkedCards(cards)
      .forEach((cardId) => internalActions.cardToFront(cardId));
  },
  updateCardLinkedMarks() {
    useLinkStore()
      .updateCardLinkedMarks()
      .forEach((cardId) => internalActions.cardToFront(cardId));
  },

  moveCard(boardId: string, cardId: string, pos: RelativeCoordinate, stopAlter = false) {
    writer.moveCard(boardId, cardId, pos);
    if (stopAlter) {
      internalActions.stopAlter(cardId, pos);
    }
  },

  pointerPos(boardId: string, pos: RelativeCoordinate) {
    writer.pointerPos(boardId, pos);
  },

  addMeOnBoard(boardId: string) {
    writer.addUserOnBoard(boardId, useUserStore().technicalUser.id);
  },

  stopAlter(id: string, pos?: RelativeCoordinate) {
    const card = useBoardStore().currentBoard().cards[id];
    // could have just been deleted
    // normally -> don't stop alter when card is selected
    // moving multiple cards -> they are still selected, but we need a stop alter
    if (card && (pos || !useBoardStore().isStickySelected(id))) {
      writer.stopAlterCard(useBoardStore().currentBoard().id, id, {
        type: card.data.type,
        ...(pos || card.meta.pos),
      });
    }
  },

  /**
   * If the card is currently locked by the user (editing or selected),
   * send alterCard. Otherwise, send stopAlterCard.
   *
   * This ensures that the changes are persisted, but the card isn't unlocked accidentally
   */
  alterCard(
    boardId: string,
    id: string,
    props: Partial<Card & RelativeCoordinate>,
    priorities?: boolean,
  ) {
    if (useBoardStore().isStickyIdSelectedOrActive(id)) {
      writer.alterCard(boardId, id, props, priorities);
    } else {
      writer.stopAlterCard(boardId, id, props, priorities);
    }
  },

  setIteration(id: string, iterationId: number | null) {
    if (useCardStore().setIteration({ id, iterationId })) {
      writer.alterCard(useBoardStore().currentBoard().id, id, { iterationId });
    }
  },

  setTeam(cardId: string, teamId: string | null) {
    useCardStore().setTeam({ id: cardId, teamId });
    internalActions.alterCard(useBoardStore().currentBoard().id, cardId, { teamId });
  },

  setArt(cardId: string, artId: string | null) {
    useCardStore().setArt({ id: cardId, artId });
    internalActions.alterCard(useBoardStore().currentBoard().id, cardId, { artId });
  },

  setCardSize(boardId: string, factor: number) {
    const val = clamp(factor, minCardSizeFactor, maxCardSizeFactor);
    if (useBoardsStore().setCardSize({ boardId, factor: val })) {
      writer.scaleBoard(boardId, val);
    }
  },
};

function cleanupBoard(board: Board | undefined, cards: BoardCard[], newBoard: boolean) {
  if (board) {
    for (const card of cards) {
      writer.stopAlterCard(board.id, card.data.id, { type: card.data.type });
    }
    writer.pointerPos(board.id, offScreenCoord());
    clearStickySelection(board);
  }
  if (newBoard) {
    void removeBoardSubscriptions();
  }
}

function clearStickySelection(board: Board) {
  for (const stickyId in board.selected) {
    useBoardStore().unselectSticky(stickyId, board);
    writer.stopAlterCard(board.id, stickyId);
  }
}
